import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientDTO, RoleRequestDTO } from '../../../core/models/role.model';
import { RoleService } from '../../../core/services/role.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-role-form',
  imports: [
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressBarModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
    MatPaginatorModule,
    MatTableModule,
    MatProgressSpinnerModule
],
  templateUrl: './role-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-form.component.scss']
})
export class RoleFormComponent implements OnInit, OnDestroy {
  roleForm!: FormGroup;
  clients: ClientDTO[] = [];
  isEditMode = false;
  isRealmRole = false;
  loading = false;
  submitting = false;

  originalRoleName = '';
  clientId = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private roleService: RoleService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadClients();
    this.checkRouteParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.roleForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z0-9_-]+$/)
      ]],
      description: ['', [Validators.maxLength(255)]],
      title: ['', [Validators.maxLength(100)]],
      roleType: ['client', Validators.required],
      clientId: ['', Validators.required],
      composite: [false]
    });

    this.roleForm.get('roleType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.isRealmRole = type === 'realm';
        const clientControl = this.roleForm.get('clientId');
        if (this.isRealmRole) {
          clientControl?.clearValidators();
          clientControl?.setValue('');
        } else {
          clientControl?.setValidators(Validators.required);
        }
        clientControl?.updateValueAndValidity();
      });
  }

  private loadClients(): void {
    this.roleService.getClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clients = response.content.filter(c => c.enabled);
        },
        error: (error) => {
          console.error('Erreur lors du chargement des clients:', error);
          this.showError('Erreur lors du chargement des clients');
        }
      });
  }

  private checkRouteParams(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const roleName = params['roleName'];
        const clientId = params['clientId'];
        if (roleName) {
          this.isEditMode = true;
          this.originalRoleName = roleName;
          this.clientId = clientId;
          this.isRealmRole = !clientId;
          this.loadRoleData(roleName, clientId);
        }
      });
  }

  private loadRoleData(roleName: string, clientId?: string): void {
    this.loading = true;
    const observable = clientId
      ? this.roleService.getClientRoleDetails(clientId, roleName)
      : this.roleService.getRealmRoleDetails(roleName);
    observable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (role) => {
          if (role) {
            this.roleForm.patchValue({
              name: role.name,
              description: role.description || '',
              title: role.title || '',
              roleType: clientId ? 'client' : 'realm',
              clientId: clientId || '',
              composite: role.composite
            });
            this.roleForm.get('name')?.disable();
            this.roleForm.get('roleType')?.disable();
            this.roleForm.get('clientId')?.disable();
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement du rôle:', error);
          this.showError('Erreur lors du chargement du rôle');
          this.loading = false;
          this.goBack();
        }
      });
  }

  onSubmit(): void {
    if (this.roleForm.invalid) {
      this.markFormGroupTouched(this.roleForm);
      this.showError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    this.submitting = true;
    const formValue = this.roleForm.getRawValue();
    const roleRequest: RoleRequestDTO = {
      name: formValue.name,
      description: formValue.description || undefined,
      composite: formValue.composite,
      title: formValue.title || undefined
    };

    if (this.isEditMode) {
      this.updateRole(roleRequest);
    } else {
      this.createRole(roleRequest, formValue.roleType, formValue.clientId);
    }
  }

  private createRole(roleRequest: RoleRequestDTO, roleType: string, clientId: string): void {
    this.roleService.roleExists(roleRequest.name, roleType === 'client' ? clientId : undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (exists) => {
          if (exists) {
            this.showError(`Un rôle avec le nom "${roleRequest.name}" existe déjà`);
            this.submitting = false;
            return;
          }
          const createObservable = roleType === 'realm'
            ? this.roleService.createRealmRole(roleRequest)
            : this.roleService.createClientRole(clientId, roleRequest);
          createObservable
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.showSuccess(`Rôle "${roleRequest.name}" créé avec succès`);
                this.goBack();
              },
              error: (error) => {
                console.error('Erreur lors de la création:', error);
                this.handleError(error);
                this.submitting = false;
              }
            });
        },
        error: (error) => {
          console.error('Erreur lors de la vérification:', error);
          this.submitting = false;
        }
      });
  }

  private updateRole(roleRequest: RoleRequestDTO): void {
    const updateObservable = this.isRealmRole
      ? this.roleService.updateRealmRole(this.originalRoleName, roleRequest)
      : this.roleService.updateClientRole(this.clientId, this.originalRoleName, roleRequest);
    updateObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccess('Rôle mis à jour avec succès');
          this.goBack();
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour:', error);
          this.handleError(error);
          this.submitting = false;
        }
      });
  }

  private handleError(error: any): void {
    if (error.status === 409) {
      this.showError('Un rôle avec ce nom existe déjà');
    } else if (error.status === 400) {
      this.showError('Données invalides. Vérifiez le formulaire');
    } else if (error.error?.message) {
      this.showError(error.error.message);
    } else {
      this.showError('Une erreur est survenue. Veuillez réessayer');
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.roleForm.get(fieldName);
    if (!control || !control.touched) return '';
    if (control.hasError('required')) return 'Ce champ est requis';
    if (control.hasError('minlength')) {
      const minLength = control.errors?.['minlength'].requiredLength;
      return `Minimum ${minLength} caractères`;
    }
    if (control.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength'].requiredLength;
      return `Maximum ${maxLength} caractères`;
    }
    if (control.hasError('pattern')) {
      return 'Seuls les lettres, chiffres, tirets et underscores sont autorisés';
    }
    return '';
  }

  hasError(fieldName: string): boolean {
    const control = this.roleForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  goBack(): void {
    this.router.navigate(['/dashboard/roles']);
  }

  getPageTitle(): string {
    return this.isEditMode ? 'Modifier le rôle' : 'Créer un nouveau rôle';
  }

  getSubmitButtonLabel(): string {
    if (this.submitting) {
      return this.isEditMode ? 'Mise à jour...' : 'Création...';
    }
    return this.isEditMode ? 'Mettre à jour' : 'Créer le rôle';
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}